import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { clearCatalogMirror } from "@/lib/catalogMirror";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "lhc_token";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  // status: 'loading' | 'authenticated' | 'anonymous'
  const [status, setStatus] = useState("loading");

  const setAuthHeader = (token) => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common["Authorization"];
    }
  };

  const fetchMe = useCallback(async (token) => {
    setAuthHeader(token);
    const { data } = await axios.get(`${API}/auth/me`);
    setUser(data);
    setStatus("authenticated");
    return data;
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    // Drop any prior tenant's cached catalog mirror before the new session loads its own,
    // so a dealer can never inherit another dealer's stale prices/models from localStorage.
    clearCatalogMirror();
    localStorage.setItem(TOKEN_KEY, data.token);
    setAuthHeader(data.token);
    setUser(data.user);
    setStatus("authenticated");
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      try {
        await axios.post(`${API}/auth/logout`);
      } catch (_) {
        /* token may already be expired — ignore */
      }
    }
    localStorage.removeItem(TOKEN_KEY);
    clearCatalogMirror(); // never leave a tenant's catalog/prices in localStorage after logout
    setAuthHeader(null);
    setUser(null);
    setStatus("anonymous");
  }, []);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setStatus("anonymous");
      return;
    }
    fetchMe(token).catch(() => {
      localStorage.removeItem(TOKEN_KEY);
      setAuthHeader(null);
      setUser(null);
      setStatus("anonymous");
    });
  }, [fetchMe]);

  return (
    <AuthContext.Provider value={{ user, status, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
